export type {
  PlaceholderShapeKind,
  PlaceholderShapeParams,
  PlaceholderFitMode,
  PlaceholderShapeConfig,
  RawSlotMeta,
  ResolvedPlaceholderShape,
  Point2,
} from "./types";

export {
  buildPlaceholderBoundary,
  pointInPolygon,
  appendBoundaryToCanvasPath,
  boundaryToPath2D,
  boundaryToSvgPathD,
  boundaryToCssClipPathValue,
} from "./geometry";

export {
  normalizeSlotShape,
  inferShapeFromLegacy,
  resolveShapeParams,
  coerceShapeKind,
  roundednessFromLegacyBorderRadiusPercent,
  legacyApertureFromShape,
} from "./normalize";

import type { RawSlotMeta } from "./types";
import {
  buildPlaceholderBoundary,
  appendBoundaryToCanvasPath,
  boundaryToSvgPathD,
  boundaryToCssClipPathValue,
} from "./geometry";
import { normalizeSlotShape } from "./normalize";
import type { ResolvedPlaceholderShape } from "./types";

/** Clip canvas ctx — call inside ctx.save() after translate( center ) + rotate( slotRotation ). */
export function clipPlaceholderAperture(
  ctx: CanvasRenderingContext2D,
  slotWidth: number,
  slotHeight: number,
  slotMeta: RawSlotMeta | undefined,
  boundaryOpts?: import("./geometry").BuildBoundaryOptions
): void {
  const { kind, params } = normalizeSlotShape(slotMeta);
  const resolved: ResolvedPlaceholderShape = { kind, params };
  const poly = buildPlaceholderBoundary(
    kind,
    slotWidth,
    slotHeight,
    resolved,
    boundaryOpts
  );
  ctx.beginPath();
  appendBoundaryToCanvasPath(ctx, poly);
  ctx.clip();
}

/** SVG for Sharp mask: white opaque inside, transparent outside. */
export function buildApertureMaskSvg(
  width: number,
  height: number,
  slotMeta: RawSlotMeta | undefined
): string {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const { kind, params } = normalizeSlotShape(slotMeta);
  const resolved: ResolvedPlaceholderShape = { kind, params };
  const poly = buildPlaceholderBoundary(kind, w, h, resolved);
  const d = boundaryToSvgPathD(poly, w, h);
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <path d="${d}" fill="white" fill-rule="evenodd"/>
</svg>`;
}

/** CSS clip for DOM layers; `null` when axis-aligned rectangle (use overflow:hidden only). */
export function getPlaceholderClipCss(
  width: number,
  height: number,
  slotMeta: RawSlotMeta | undefined
): { clipPath: string; WebkitClipPath: string } | null {
  const r = normalizeSlotShape(slotMeta);
  if (r.kind === "rect" && r.params.waviness < 1e-6) {
    return null;
  }
  const poly = buildPlaceholderBoundary(r.kind, width, height, {
    kind: r.kind,
    params: r.params,
  });
  const v = boundaryToCssClipPathValue(poly, Math.max(1, width), Math.max(1, height));
  return { clipPath: v, WebkitClipPath: v };
}
